import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";

export const PrivacyPolicy = (): JSX.Element => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/join')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <Button 
              className="w-[166px] h-[49px] bg-[#daed6e] rounded-[20px] border border-solid border-[#e7e7e7] hover:bg-[#c5d85c]"
              onClick={() => navigate('/join')}
            >
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">Back</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-16">
        <div className="max-w-4xl mx-auto bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8">
            <h1 className="font-['Space_Grotesk'] font-bold text-3xl text-black mb-4">
              Privacy Policy
            </h1>
            <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>

          <div className="space-y-6 font-['Space_Grotesk'] text-black">
            <section>
              <h2 className="font-bold text-xl mb-3">1. Information We Collect</h2>
              <p className="text-base leading-relaxed mb-3">
                We collect information you provide directly to us, such as when you create an account, use our services, or contact us. This may include:
              </p>
              <ul className="list-disc list-inside space-y-2 text-base leading-relaxed">
                <li>Name and email address for account creation</li>
                <li>Usage data and preferences</li>
                <li>Communication preferences</li>
                <li>Technical information about your device and usage</li>
              </ul>
            </section>

            <section>
              <h2 className="font-bold text-xl mb-3">2. How We Use Your Information</h2>
              <p className="text-base leading-relaxed mb-3">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-base leading-relaxed">
                <li>Provide, maintain, and improve our services</li>
                <li>Process transactions and send related information</li>
                <li>Send technical notices, updates, and support messages</li>
                <li>Respond to your comments and questions</li>
                <li>Monitor and analyze trends and usage</li>
              </ul>
            </section>

            <section>
              <h2 className="font-bold text-xl mb-3">3. Information Sharing</h2>
              <p className="text-base leading-relaxed">
                We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy. We may share your information in the following circumstances:
              </p>
              <ul className="list-disc list-inside space-y-2 text-base leading-relaxed mt-3">
                <li>With your consent</li>
                <li>To comply with legal obligations</li>
                <li>To protect our rights and safety</li>
                <li>In connection with a business transfer or merger</li>
              </ul>
            </section>

            <section>
              <h2 className="font-bold text-xl mb-3">4. Data Security</h2>
              <p className="text-base leading-relaxed">
                We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="font-bold text-xl mb-3">5. Cookies and Tracking</h2>
              <p className="text-base leading-relaxed">
                We may use cookies and similar tracking technologies to enhance your experience on our platform. You can control cookie settings through your browser preferences.
              </p>
            </section>

            <section>
              <h2 className="font-bold text-xl mb-3">6. Third-Party Services</h2>
              <p className="text-base leading-relaxed">
                Our platform may integrate with third-party services (such as authentication providers). These services have their own privacy policies, and we encourage you to review them.
              </p>
            </section>

            <section>
              <h2 className="font-bold text-xl mb-3">7. Data Retention</h2>
              <p className="text-base leading-relaxed">
                We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this policy, unless a longer retention period is required by law.
              </p>
            </section>

            <section>
              <h2 className="font-bold text-xl mb-3">8. Your Rights</h2>
              <p className="text-base leading-relaxed mb-3">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-base leading-relaxed">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Object to processing of your information</li>
                <li>Withdraw consent where applicable</li>
              </ul>
            </section>

            <section>
              <h2 className="font-bold text-xl mb-3">9. Children's Privacy</h2>
              <p className="text-base leading-relaxed">
                Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.
              </p>
            </section>

            <section>
              <h2 className="font-bold text-xl mb-3">10. International Transfers</h2>
              <p className="text-base leading-relaxed">
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information.
              </p>
            </section>

            <section>
              <h2 className="font-bold text-xl mb-3">11. Changes to This Policy</h2>
              <p className="text-base leading-relaxed">
                We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="font-bold text-xl mb-3">12. Contact Us</h2>
              <p className="text-base leading-relaxed">
                If you have any questions about this privacy policy, please contact us through our platform or at the contact information provided in our terms of service.
              </p>
            </section>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <Button 
              className="bg-[#daed6e] rounded-[20px] hover:bg-[#c5d85c] transition-colors"
              onClick={() => navigate('/join')}
            >
              <span className="font-['Space_Grotesk'] font-medium text-black text-base">Back to Join</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}; 